import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { GroupEntity } from '@backstage/catalog-model';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Grid,
  TextField,
  Typography,
  Chip,
} from '@material-ui/core';
import PeopleIcon from '@material-ui/icons/People';
import AddIcon from '@material-ui/icons/Add';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(6, 4),
    maxWidth: 800,
    margin: '0 auto',
    width: '100%',
  },
  heading: {
    marginBottom: theme.spacing(1),
    textAlign: 'center',
  },
  subheading: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  teamGrid: {
    width: '100%',
    marginBottom: theme.spacing(3),
  },
  teamCard: {
    cursor: 'pointer',
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 0.15s',
    height: '100%',
  },
  teamCardSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardTitle: {
    fontWeight: 600,
  },
  metaChip: {
    marginRight: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  joinedBadge: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: '#4caf50',
  },
  prLink: {
    marginTop: theme.spacing(1),
    fontSize: '0.8rem',
  },
  createSection: {
    width: '100%',
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  createHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  formGrid: {
    marginTop: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    width: '100%',
  },
}));

interface Props {
  currentTeamSlug?: string;
  onJoinTeam: (slug: string) => Promise<{ prUrl?: string; alreadyMember?: boolean }>;
  onCreateTeam: (data: {
    name: string;
    slug: string;
    description: string;
    lead: string;
  }) => Promise<{ prUrl: string }>;
  onNext: () => void;
}

interface TeamCardState {
  joining: boolean;
  prUrl?: string;
  error?: string;
}

export function TeamSelectStep({ currentTeamSlug, onJoinTeam, onCreateTeam, onNext }: Props) {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [teams, setTeams] = useState<GroupEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [cardState, setCardState] = useState<Record<string, TeamCardState>>({});
  const [selectedSlug, setSelectedSlug] = useState<string | undefined>(currentTeamSlug);
  const [showCreate, setShowCreate] = useState(false);

  // Create team form state
  const [createName, setCreateName] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createLead, setCreateLead] = useState('');
  const [creating, setCreating] = useState(false);
  const [createPrUrl, setCreatePrUrl] = useState<string | undefined>();
  const [createError, setCreateError] = useState<string | undefined>();

  useEffect(() => {
    catalogApi
      .getEntities({ filter: { kind: 'Group', 'spec.type': 'team' } })
      .then(response => setTeams(response.items as GroupEntity[]))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [catalogApi]);

  // Auto-derive slug from name
  const handleNameChange = (value: string) => {
    setCreateName(value);
    setCreateSlug(value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
  };

  async function handleJoin(slug: string) {
    setCardState(s => ({ ...s, [slug]: { joining: true } }));
    try {
      const result = await onJoinTeam(slug);
      setCardState(s => ({
        ...s,
        [slug]: { joining: false, prUrl: result.prUrl },
      }));
      setSelectedSlug(slug);
    } catch (err: any) {
      setCardState(s => ({
        ...s,
        [slug]: { joining: false, error: err.message },
      }));
    }
  }

  async function handleCreate() {
    if (!createName || !createSlug) return;
    setCreating(true);
    setCreateError(undefined);
    try {
      const result = await onCreateTeam({
        name: createName,
        slug: createSlug,
        description: createDesc,
        lead: createLead,
      });
      setCreatePrUrl(result.prUrl);
      setSelectedSlug(createSlug);
    } catch (err: any) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  }

  const canAdvance = !!selectedSlug;

  if (loading) {
    return (
      <Box className={classes.root}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Typography variant="h5" className={classes.heading}>
        Join a Team
      </Typography>
      <Typography variant="body1" className={classes.subheading}>
        Select an existing team to join, or create a new one. You must be on at
        least one team to proceed.
      </Typography>

      {teams.length === 0 ? (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
          No teams exist yet — create the first one below.
        </Typography>
      ) : (
        <Grid container spacing={2} className={classes.teamGrid}>
          {teams.map(team => {
            const slug = team.metadata.name;
            const state = cardState[slug] ?? {};
            const isSelected = selectedSlug === slug;
            const memberCount = (team.spec?.members as string[] | undefined)?.length ?? 0;
            const lead = team.metadata.annotations?.['kyndemo.live/team-lead'];

            return (
              <Grid item xs={12} sm={6} md={4} key={slug}>
                <Card
                  className={`${classes.teamCard} ${isSelected ? classes.teamCardSelected : ''}`}
                  elevation={isSelected ? 3 : 1}
                >
                  <CardContent>
                    <Box className={classes.cardHeader}>
                      <PeopleIcon color="action" fontSize="small" />
                      <Typography variant="subtitle1" className={classes.cardTitle}>
                        {team.spec?.profile?.displayName ?? slug}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {team.metadata.description ?? ''}
                    </Typography>

                    <Box>
                      <Chip
                        size="small"
                        label={`${memberCount} member${memberCount !== 1 ? 's' : ''}`}
                        className={classes.metaChip}
                      />
                      {lead && (
                        <Chip
                          size="small"
                          label={`Lead: ${lead}`}
                          className={classes.metaChip}
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {isSelected && (state.prUrl || !state.joining) ? (
                      <Box className={classes.joinedBadge}>
                        <CheckCircleIcon fontSize="small" />
                        <Typography variant="body2">Joined</Typography>
                        {state.prUrl && (
                          <a
                            href={state.prUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.prLink}
                          >
                            View PR <OpenInNewIcon fontSize="inherit" />
                          </a>
                        )}
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        style={{ marginTop: 12 }}
                        disabled={state.joining}
                        onClick={() => handleJoin(slug)}
                        startIcon={state.joining ? <CircularProgress size={14} /> : undefined}
                      >
                        {state.joining ? 'Requesting…' : 'Join Team'}
                      </Button>
                    )}

                    {state.error && (
                      <Typography variant="caption" color="error">
                        {state.error}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create new team section */}
      <Box className={classes.createSection}>
        <Box
          className={classes.createHeader}
          onClick={() => setShowCreate(v => !v)}
        >
          <Typography variant="subtitle1">
            <AddIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Create a new team
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {showCreate ? 'collapse' : 'expand'}
          </Typography>
        </Box>

        <Collapse in={showCreate}>
          {createPrUrl ? (
            <Box style={{ marginTop: 16 }}>
              <Box className={classes.joinedBadge}>
                <CheckCircleIcon fontSize="small" />
                <Typography variant="body2">Team created — PR opened</Typography>
                <a
                  href={createPrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.prLink}
                >
                  View PR <OpenInNewIcon fontSize="inherit" />
                </a>
              </Box>
            </Box>
          ) : (
            <Grid container spacing={2} className={classes.formGrid}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Team name"
                  fullWidth
                  size="small"
                  value={createName}
                  onChange={e => handleNameChange(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Slug (auto-derived)"
                  fullWidth
                  size="small"
                  value={createSlug}
                  onChange={e => setCreateSlug(e.target.value)}
                  helperText="Lowercase, numbers, hyphens only"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  size="small"
                  value={createDesc}
                  onChange={e => setCreateDesc(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Team lead (Backstage username)"
                  fullWidth
                  size="small"
                  value={createLead}
                  onChange={e => setCreateLead(e.target.value)}
                />
              </Grid>
              {createError && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error">
                    {createError}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleCreate}
                  disabled={creating || !createName || !createSlug}
                  startIcon={creating ? <CircularProgress size={14} /> : <AddIcon />}
                >
                  {creating ? 'Creating…' : 'Create & Join'}
                </Button>
              </Grid>
            </Grid>
          )}
        </Collapse>
      </Box>

      <Box className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!canAdvance}
          onClick={onNext}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}
